'use client';

import { c as _c } from "react/compiler-runtime";
import { Menu, Search, ShoppingBag, User, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { Suspense, startTransition, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCartSummary } from '@/components/cart/cart-provider';
import type { CartSettingsConfig } from '@/lib/cart-settings';
import { toCanonicalPath } from '@/lib/markets';
import type { AccountSettings } from '@/lib/site-settings';
import { useBrowserLocation } from '@/lib/use-browser-location';
import { useHeaderRuntime } from './header-runtime';
import { getAccountDrawerErrorMessage, type MobileLearnLink, type MobileNeedHelpLink, type NavItem, type PopularProduct, type TrendingSearch } from './site-header.shared';

/**
 * Runs synchronously while the header HTML is parsed — before first paint and
 * before any JavaScript bundle loads — and copies the `cartQty` cookie (kept in
 * sync by CartProvider) into the badge rendered just before it. Kept
 * dependency-free and wrapped in try/catch; a missing/invalid cookie leaves the
 * server-rendered `0` (hidden) in place.
 */
const CART_COUNT_PREPAINT_SCRIPT = `(function(){try{var m=document.cookie.match(/(?:^|;\\s*)cartQty=(\\d+)/);var n=m?parseInt(m[1],10):0;if(!(n>0))return;var s=document.currentScript;var b=s&&s.previousElementSibling;var el=b&&b.querySelector('[data-cart-count]');if(!el)return;el.textContent=String(n);el.setAttribute('data-cart-count',String(n));}catch(e){}})();`;
const loadMobileMenu = () => import('./mobile-menu');
const MobileMenu = dynamic(() => loadMobileMenu().then(module => module.MobileMenu), {
  ssr: false
});
const loadSearchPopdown = () => import('@/components/search/search-popdown');
const SearchPopdown = dynamic(() => loadSearchPopdown().then(module => module.SearchPopdown), {
  ssr: false
});
const loadCartDrawer = () => import('@/components/cart/cart-drawer');
const CartDrawer = dynamic(() => loadCartDrawer().then(module => module.CartDrawer), {
  ssr: false
});
const loadAccountDrawer = () => import('@/components/account/account-drawer');
const AccountDrawer = dynamic(() => loadAccountDrawer().then(module => module.AccountDrawer), {
  ssr: false
});
interface SiteHeaderControlsProps {
  logo: ReactNode;
  desktopNavigation: ReactNode;
  logoText?: string | null;
  logoUrl?: string | null;
  mobileNeedHelpHeading?: string;
  mobileNeedHelpLinks?: MobileNeedHelpLink[];
  mobileShowAccountLink?: boolean;
  mobileLearnHeading?: string;
  mobileLearnLinks?: MobileLearnLink[];
  navigation?: NavItem[];
  cartSettings: CartSettingsConfig;
  accountSettings?: AccountSettings;
  enableCustomerAccounts?: boolean;
  enableReviews?: boolean;
  popularProducts?: PopularProduct[];
  popularProductHandles?: string[];
  popularProductsHeading?: string;
  trendingSearches?: TrendingSearch[];
}
function getNewlyOpenedPanel({
  accountDrawerOpen,
  cartOpen,
  mobileMenuOpen,
  previousAccountDrawerOpen,
  previousCartOpen,
  previousMobileMenuOpen
}: {
  accountDrawerOpen: boolean;
  cartOpen: boolean;
  mobileMenuOpen: boolean;
  previousAccountDrawerOpen: boolean;
  previousCartOpen: boolean;
  previousMobileMenuOpen: boolean;
}) {
  if (cartOpen && !previousCartOpen) {
    return 'cart';
  }
  if (mobileMenuOpen && !previousMobileMenuOpen) {
    return 'menu';
  }
  if (accountDrawerOpen && !previousAccountDrawerOpen) {
    return 'account';
  }
  return null;
}
function CartQueryOpener(t0) {
  const $ = _c(8);
  const {
    onOpenCart
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const search = t1;
  let t2;
  let t3;
  if ($[2] !== onOpenCart || $[3] !== pathname || $[4] !== router || $[5] !== search) {
    t2 = () => {
      if (!pathname) {
        return;
      }
      const searchParams_0 = new URLSearchParams(search);
      if (searchParams_0.get("cart") !== "open") {
        return;
      }
      onOpenCart();
      const nextParams = new URLSearchParams(searchParams_0.toString());
      nextParams.delete("cart");
      const nextUrl = nextParams.size > 0 ? `${pathname}?${nextParams.toString()}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    t3 = [onOpenCart, pathname, router, search];
    $[2] = onOpenCart;
    $[3] = pathname;
    $[4] = router;
    $[5] = search;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  return null;
}
export function SiteHeaderControls(t0) {
  const $ = _c(111);
  const {
    logo,
    desktopNavigation,
    logoText,
    logoUrl,
    mobileNeedHelpHeading,
    mobileNeedHelpLinks,
    mobileShowAccountLink,
    mobileLearnHeading,
    mobileLearnLinks,
    navigation: t1,
    cartSettings: staticCartSettings,
    accountSettings,
    enableCustomerAccounts: t2,
    enableReviews: t3,
    popularProducts: t4,
    popularProductHandles: t5,
    popularProductsHeading,
    trendingSearches
  } = t0;
  let t6;
  if ($[0] !== t1) {
    t6 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const navigation = t6;
  const staticEnableCustomerAccounts = t2 === undefined ? false : t2;
  const staticEnableReviews = t3 === undefined ? false : t3;
  let t7;
  if ($[2] !== t4) {
    t7 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const popularProducts = t7;
  let t8;
  if ($[4] !== t5) {
    t8 = t5 === undefined ? [] : t5;
    $[4] = t5;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const popularProductHandles = t8;
  const headerRuntime = useHeaderRuntime();
  const cartSettings = headerRuntime?.cartSettings ?? staticCartSettings;
  const enableCustomerAccounts = headerRuntime?.enableCustomerAccounts ?? staticEnableCustomerAccounts;
  const enableReviews = headerRuntime?.enableReviews ?? staticEnableReviews;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [accountDrawerError, setAccountDrawerError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hasMountedMobileMenu, setHasMountedMobileMenu] = useState(false);
  const [hasMountedSearch, setHasMountedSearch] = useState(false);
  const [hasMountedCartDrawer, setHasMountedCartDrawer] = useState(false);
  const [hasMountedAccountDrawer, setHasMountedAccountDrawer] = useState(false);
  const {
    quantity: cartQuantity,
    isOpen: cartOpen,
    openCart,
    closeCart
  } = useCartSummary();
  const {
    pathname
  } = useBrowserLocation();
  const router = useRouter();
  const shellRef = useRef(null);
  const previousCartOpenRef = useRef(cartOpen);
  const previousMobileMenuOpenRef = useRef(mobileMenuOpen);
  const previousAccountDrawerOpenRef = useRef(accountDrawerOpen);
  const [headerOffset, setHeaderOffset] = useState(44);
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = t10 => {
      const errorMessage = t10 === undefined ? null : t10;
      setHasMountedAccountDrawer(true);
      setAccountDrawerError(errorMessage);
      setAccountDrawerOpen(true);
    };
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const openAccountDrawer = t9;
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setAccountDrawerOpen(false);
      setAccountDrawerError(null);
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const closeAccountDrawer = t10;
  let t11;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setHasMountedMobileMenu(true);
      setMobileMenuOpen(_temp);
    };
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const toggleMobileMenu = t11;
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setHasMountedSearch(true);
      setSearchOpen(true);
    };
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const openSearch = t12;
  let t13;
  if ($[10] !== openCart) {
    t13 = () => {
      setHasMountedCartDrawer(true);
      openCart();
    };
    $[10] = openCart;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const handleOpenCart = t13;
  let t14;
  let t15;
  if ($[12] !== accountDrawerOpen || $[13] !== cartOpen || $[14] !== closeCart || $[15] !== mobileMenuOpen) {
    t14 = function syncHeaderOverlays() {
      bb57: switch (getNewlyOpenedPanel({
        accountDrawerOpen,
        cartOpen,
        mobileMenuOpen,
        previousAccountDrawerOpen: previousAccountDrawerOpenRef.current,
        previousCartOpen: previousCartOpenRef.current,
        previousMobileMenuOpen: previousMobileMenuOpenRef.current
      })) {
        case "cart":
          {
            if (mobileMenuOpen) {
              setMobileMenuOpen(false);
            }
            if (accountDrawerOpen) {
              closeAccountDrawer();
            }
            break bb57;
          }
        case "menu":
          {
            if (cartOpen) {
              closeCart();
            }
            if (accountDrawerOpen) {
              closeAccountDrawer();
            }
            break bb57;
          }
        case "account":
          {
            if (mobileMenuOpen) {
              setMobileMenuOpen(false);
            }
            if (cartOpen) {
              closeCart();
            }
          }
      }
      previousCartOpenRef.current = cartOpen;
      previousMobileMenuOpenRef.current = mobileMenuOpen;
      previousAccountDrawerOpenRef.current = accountDrawerOpen;
    };
    t15 = [cartOpen, mobileMenuOpen, accountDrawerOpen, closeAccountDrawer, closeCart];
    $[12] = accountDrawerOpen;
    $[13] = cartOpen;
    $[14] = closeCart;
    $[15] = mobileMenuOpen;
    $[16] = t14;
    $[17] = t15;
  } else {
    t14 = $[16];
    t15 = $[17];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[18] !== pathname) {
    t16 = () => {
      if (!pathname) {
        return;
      }
      setMobileMenuOpen(false);
      setSearchOpen(false);
    };
    t17 = [pathname];
    $[18] = pathname;
    $[19] = t16;
    $[20] = t17;
  } else {
    t16 = $[19];
    t17 = $[20];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[21] !== enableCustomerAccounts || $[22] !== pathname || $[23] !== router) {
    t18 = () => {
      if (!enableCustomerAccounts || typeof window === "undefined" || !pathname) {
        return;
      }
      const searchParams = new URLSearchParams(window.location.search);
      const callbackErrorMessage = getAccountDrawerErrorMessage(searchParams.get("error"));
      const shouldOpenFromTrackedShopifyLanding = pathname === "/" && searchParams.has("syclid");
      const shouldOpenAccountDrawer = searchParams.get("account") === "open" || callbackErrorMessage !== null || shouldOpenFromTrackedShopifyLanding;
      if (!shouldOpenAccountDrawer) {
        return;
      }
      openAccountDrawer(callbackErrorMessage);
      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.delete("account");
      nextParams.delete("error");
      if (shouldOpenFromTrackedShopifyLanding) {
        nextParams.delete("syclid");
      }
      const nextUrl = nextParams.size > 0 ? `${toCanonicalPath(window.location.pathname)}?${nextParams.toString()}` : toCanonicalPath(window.location.pathname);
      router.replace(nextUrl, {
        scroll: false
      });
    };
    t19 = [enableCustomerAccounts, openAccountDrawer, pathname, router];
    $[21] = enableCustomerAccounts;
    $[22] = pathname;
    $[23] = router;
    $[24] = t18;
    $[25] = t19;
  } else {
    t18 = $[24];
    t19 = $[25];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[26] !== mobileMenuOpen) {
    t20 = () => {
      if (mobileMenuOpen) {
        setHasMountedMobileMenu(true);
      }
    };
    t21 = [mobileMenuOpen];
    $[26] = mobileMenuOpen;
    $[27] = t20;
    $[28] = t21;
  } else {
    t20 = $[27];
    t21 = $[28];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[29] !== enableCustomerAccounts) {
    t22 = () => {
      let cancelled = false;
      const preload = () => {
        const isMobile = window.matchMedia("(max-width: 1023px)").matches;
        const mount = setMounted => () => {
          if (!cancelled) {
            startTransition(() => setMounted(true));
          }
        };
        if (isMobile) {
          loadMobileMenu().then(mount(setHasMountedMobileMenu));
        }
        loadSearchPopdown().then(mount(setHasMountedSearch));
        loadCartDrawer().then(mount(setHasMountedCartDrawer));
        if (enableCustomerAccounts) {
          loadAccountDrawer().then(mount(setHasMountedAccountDrawer));
        }
      };
      if (typeof requestIdleCallback !== "function") {
        const timer = setTimeout(preload, 1500);
        return () => {
          cancelled = true;
          clearTimeout(timer);
        };
      }
      const idle = requestIdleCallback(preload, {
        timeout: 3000
      });
      return () => {
        cancelled = true;
        cancelIdleCallback(idle);
      };
    };
    t23 = [enableCustomerAccounts];
    $[29] = enableCustomerAccounts;
    $[30] = t22;
    $[31] = t23;
  } else {
    t22 = $[30];
    t23 = $[31];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[32] !== searchOpen) {
    t24 = () => {
      if (searchOpen) {
        setHasMountedSearch(true);
      }
    };
    t25 = [searchOpen];
    $[32] = searchOpen;
    $[33] = t24;
    $[34] = t25;
  } else {
    t24 = $[33];
    t25 = $[34];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[35] !== cartOpen) {
    t26 = () => {
      if (cartOpen) {
        setHasMountedCartDrawer(true);
      }
    };
    t27 = [cartOpen];
    $[35] = cartOpen;
    $[36] = t26;
    $[37] = t27;
  } else {
    t26 = $[36];
    t27 = $[37];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[38] !== accountDrawerOpen) {
    t28 = () => {
      if (accountDrawerOpen) {
        setHasMountedAccountDrawer(true);
      }
    };
    t29 = [accountDrawerOpen];
    $[38] = accountDrawerOpen;
    $[39] = t28;
    $[40] = t29;
  } else {
    t28 = $[39];
    t29 = $[40];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => {
      const shell = shellRef.current;
      const nav = shell?.closest("nav");
      if (!shell || !nav) {
        return;
      }
      const root = document.documentElement;
      let lastOffset = -1;
      let frame = 0;
      const measure = () => {
        frame = 0;
        const nextOffset = Math.ceil(nav.getBoundingClientRect().height);
        if (nextOffset === lastOffset) {
          return;
        }
        lastOffset = nextOffset;
        setHeaderOffset(nextOffset);
        root.style.setProperty("--site-header-offset", `${nextOffset}px`);
      };
      const scheduleMeasure = () => {
        if (frame) {
          return;
        }
        frame = window.requestAnimationFrame(measure);
      };
      const cleanUp = () => {
        if (frame) {
          window.cancelAnimationFrame(frame);
        }
        root.style.removeProperty("--site-header-offset");
        window.removeEventListener("resize", scheduleMeasure);
      };
      measure();
      window.addEventListener("resize", scheduleMeasure);
      if (typeof ResizeObserver === "undefined") {
        return cleanUp;
      }
      const resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(nav);
      return () => {
        resizeObserver.disconnect();
        cleanUp();
      };
    };
    t31 = [];
    $[41] = t30;
    $[42] = t31;
  } else {
    t30 = $[41];
    t31 = $[42];
  }
  useLayoutEffect(t30, t31);
  let t32;
  if ($[43] !== openCart) {
    t32 = <Suspense fallback={null}><CartQueryOpener onOpenCart={openCart} /></Suspense>;
    $[43] = openCart;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  const t33 = mobileMenuOpen ? "Close menu" : "Open menu";
  let t34;
  if ($[45] !== mobileMenuOpen) {
    t34 = mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />;
    $[45] = mobileMenuOpen;
    $[46] = t34;
  } else {
    t34 = $[46];
  }
  let t35;
  if ($[47] !== t33 || $[48] !== t34) {
    t35 = <button type="button" className="-ml-2 p-2 text-neutral-900 lg:hidden" onPointerDown={_temp2} onClick={toggleMobileMenu} aria-label={t33}>{t34}</button>;
    $[47] = t33;
    $[48] = t34;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] !== logo || $[51] !== t35) {
    t36 = <div className="flex items-center gap-0">{t35}{logo}</div>;
    $[50] = logo;
    $[51] = t35;
    $[52] = t36;
  } else {
    t36 = $[52];
  }
  let t37;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <button type="button" className="p-2 text-neutral-700 transition-colors hover:text-neutral-900" onPointerDown={_temp3} onClick={openSearch} aria-label="Search"><Search className="h-5 w-5" /></button>;
    $[53] = t37;
  } else {
    t37 = $[53];
  }
  let t38;
  if ($[54] !== enableCustomerAccounts) {
    t38 = enableCustomerAccounts && <button type="button" onPointerDown={_temp4} onClick={() => openAccountDrawer()} className="p-2 text-neutral-700 transition-colors hover:text-neutral-900" aria-label="Account"><User className="h-5 w-5" /></button>;
    $[54] = enableCustomerAccounts;
    $[55] = t38;
  } else {
    t38 = $[55];
  }
  let t39;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ShoppingBag className="h-5 w-5" />;
    $[56] = t39;
  } else {
    t39 = $[56];
  }
  let t40;
  if ($[57] !== cartQuantity) {
    t40 = <span data-cart-count={cartQuantity} suppressHydrationWarning={true} className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-900 text-[10px] font-bold text-white data-[cart-count=0]:hidden">{cartQuantity}</span>;
    $[57] = cartQuantity;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  let t41;
  if ($[59] !== handleOpenCart || $[60] !== t40) {
    t41 = <button type="button" className="relative p-2 text-neutral-700 transition-colors hover:text-neutral-900" onPointerDown={_temp5} onClick={handleOpenCart} aria-label="Open cart">{t39}{t40}</button>;
    $[59] = handleOpenCart;
    $[60] = t40;
    $[61] = t41;
  } else {
    t41 = $[61];
  }
  let t42;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <script dangerouslySetInnerHTML={{
      __html: CART_COUNT_PREPAINT_SCRIPT
    }} />;
    $[62] = t42;
  } else {
    t42 = $[62];
  }
  let t43;
  if ($[63] !== t38 || $[64] !== t41) {
    t43 = <div className="flex items-center gap-0.5">{t37}{t38}{t41}{t42}</div>;
    $[63] = t38;
    $[64] = t41;
    $[65] = t43;
  } else {
    t43 = $[65];
  }
  let t44;
  if ($[66] !== desktopNavigation || $[67] !== t36 || $[68] !== t43) {
    t44 = <div ref={shellRef} className="flex h-11 items-center justify-between lg:h-12">{t36}{desktopNavigation}{t43}</div>;
    $[66] = desktopNavigation;
    $[67] = t36;
    $[68] = t43;
    $[69] = t44;
  } else {
    t44 = $[69];
  }
  let t45;
  if ($[70] !== enableCustomerAccounts || $[71] !== hasMountedMobileMenu || $[72] !== headerOffset || $[73] !== mobileLearnHeading || $[74] !== mobileLearnLinks || $[75] !== mobileMenuOpen || $[76] !== mobileNeedHelpHeading || $[77] !== mobileNeedHelpLinks || $[78] !== mobileShowAccountLink || $[79] !== navigation) {
    t45 = hasMountedMobileMenu && <MobileMenu isOpen={mobileMenuOpen} needHelpHeading={mobileNeedHelpHeading} needHelpLinks={mobileNeedHelpLinks} learnHeading={mobileLearnHeading} learnLinks={mobileLearnLinks} enableCustomerAccounts={enableCustomerAccounts} showAccountLink={mobileShowAccountLink} onAccountClick={() => {
      setMobileMenuOpen(false);
      openAccountDrawer();
    }} onClose={() => setMobileMenuOpen(false)} navigation={navigation} headerOffset={headerOffset} />;
    $[70] = enableCustomerAccounts;
    $[71] = hasMountedMobileMenu;
    $[72] = headerOffset;
    $[73] = mobileLearnHeading;
    $[74] = mobileLearnLinks;
    $[75] = mobileMenuOpen;
    $[76] = mobileNeedHelpHeading;
    $[77] = mobileNeedHelpLinks;
    $[78] = mobileShowAccountLink;
    $[79] = navigation;
    $[80] = t45;
  } else {
    t45 = $[80];
  }
  let t46;
  if ($[81] !== enableReviews || $[82] !== hasMountedSearch || $[83] !== popularProductHandles || $[84] !== popularProducts || $[85] !== popularProductsHeading || $[86] !== searchOpen || $[87] !== trendingSearches) {
    t46 = hasMountedSearch && <SearchPopdown isOpen={searchOpen} onClose={() => setSearchOpen(false)} enableReviews={enableReviews} popularProducts={popularProducts} popularProductHandles={popularProductHandles} popularProductsHeading={popularProductsHeading} trendingSearches={trendingSearches} />;
    $[81] = enableReviews;
    $[82] = hasMountedSearch;
    $[83] = popularProductHandles;
    $[84] = popularProducts;
    $[85] = popularProductsHeading;
    $[86] = searchOpen;
    $[87] = trendingSearches;
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  let t47;
  if ($[89] !== cartOpen || $[90] !== cartSettings || $[91] !== closeCart || $[92] !== enableReviews || $[93] !== hasMountedCartDrawer) {
    t47 = hasMountedCartDrawer && <CartDrawer isOpen={cartOpen} onClose={closeCart} cartSettings={cartSettings} enableReviews={enableReviews} />;
    $[89] = cartOpen;
    $[90] = cartSettings;
    $[91] = closeCart;
    $[92] = enableReviews;
    $[93] = hasMountedCartDrawer;
    $[94] = t47;
  } else {
    t47 = $[94];
  }
  let t48;
  if ($[95] !== accountDrawerError || $[96] !== accountDrawerOpen || $[97] !== accountSettings?.returnsUrl || $[98] !== accountSettings?.supportEmail || $[99] !== enableCustomerAccounts || $[100] !== hasMountedAccountDrawer || $[101] !== logoText || $[102] !== logoUrl) {
    t48 = enableCustomerAccounts && hasMountedAccountDrawer && <AccountDrawer isOpen={accountDrawerOpen} onClose={closeAccountDrawer} initialErrorMessage={accountDrawerError} logoUrl={logoUrl} logoText={logoText} supportEmail={accountSettings?.supportEmail} returnsUrl={accountSettings?.returnsUrl} />;
    $[95] = accountDrawerError;
    $[96] = accountDrawerOpen;
    $[97] = accountSettings?.returnsUrl;
    $[98] = accountSettings?.supportEmail;
    $[99] = enableCustomerAccounts;
    $[100] = hasMountedAccountDrawer;
    $[101] = logoText;
    $[102] = logoUrl;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] !== t32 || $[105] !== t44 || $[106] !== t45 || $[107] !== t46 || $[108] !== t47 || $[109] !== t48) {
    t49 = <>{t32}{t44}{t45}{t46}{t47}{t48}</>;
    $[104] = t32;
    $[105] = t44;
    $[106] = t45;
    $[107] = t46;
    $[108] = t47;
    $[109] = t48;
    $[110] = t49;
  } else {
    t49 = $[110];
  }
  return t49;
}
function _temp5() {
  return void loadCartDrawer();
}
function _temp4() {
  return void loadAccountDrawer();
}
function _temp3() {
  return void loadSearchPopdown();
}
function _temp2() {
  return void loadMobileMenu();
}
function _temp(current) {
  return !current;
}